import React from 'react';

import { useListDivisionHandlesQuery } from '@/services/api/carriersApiService';

interface Props {
  carrierId: string | null | undefined;
  divisionId?: string | null | undefined;
  title?: string;
  className?: string;
}

export const DirectoryHandlesPreview: React.FC<Props> = ({
  carrierId,
  divisionId,
  title = 'Carrier Contacts',
  className,
}) => {
  // Always call hooks unconditionally; use `skip` to control fetching.
  const shouldQuery = Boolean(carrierId && divisionId);
  const { data: handles = [] } = useListDivisionHandlesQuery(
    { carrierId: carrierId ?? '', divisionId: divisionId ?? '' },
    { skip: !shouldQuery },
  );
  // Only render when a division is selected; we no longer fetch carrier-level handles here
  if (!shouldQuery) {
    return null;
  }
  const preview = handles;

  // If absolutely nothing to show, render a small empty state for clarity instead of nothing
  if (!preview || preview.length === 0) {
    return (
      <div className={className || 'rounded-md border border-gray-200 p-3'}>
        <div className="text-sm font-semibold text-gray-900 mb-1">{title}</div>
        <div className="text-xs text-gray-500">
          No handles linked to this division yet.
        </div>
      </div>
    );
  }
  return (
    <div className={className || 'rounded-md border border-gray-200 p-3'}>
      <div className="text-sm font-semibold text-gray-900 mb-2">{title}</div>
      <div className="grid gap-2">
        {preview.map((h: any) => {
          const label = h.name || h.handleType || 'Contact';
          const value =
            h.value ||
            [h.addressLine1, h.city, h.state, h.zip].filter(Boolean).join(', ');
          return (
            <div
              key={h.id}
              className="flex items-start justify-between gap-3 text-sm"
            >
              <div className="font-medium text-gray-800">{label}</div>
              <div className="text-gray-700 break-words flex-1 text-right">
                {value}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
